/**
 * GraphQL Queries y Mutations para el Módulo de Tesorería
 * 
 * NOTA: Las queries y mutations reales están comentadas.
 * Se utilizan datos dummy locales hasta conectar con el backend.
 * 
 * Tipos esperados del backend:
 * 
 * @typedef {Object} CuentaBancaria
 * @property {string} id
 * @property {string} banco
 * @property {string} descripcion
 * @property {'ARS'|'USD'|'EUR'} moneda
 * @property {'CA'|'CC'} tipo - Caja de Ahorro / Cuenta Corriente
 * @property {string} cbu
 * @property {string} alias
 * @property {string} numeroCuenta
 * @property {string} titular
 * @property {number} saldo
 * @property {'ACTIVO'|'INACTIVO'} estado
 * @property {string} observaciones
 * @property {string} createdAt
 * @property {string} updatedAt
 * 
 * @typedef {Object} MedioPago
 * @property {string} id
 * @property {string} nombre
 * @property {'EFECTIVO'|'TRANSFERENCIA'|'TARJETA'|'MONI'|'CHEQUE'|'OTRO'} tipo
 * @property {string|null} cuentaBancariaId
 * @property {CuentaBancaria|null} cuentaBancaria
 * @property {boolean} habilitado
 * @property {string} notas
 * 
 * @typedef {Object} PagoProveedor
 * @property {string} id
 * @property {string} fecha
 * @property {string} proveedorId
 * @property {Object} proveedor
 * @property {'ARS'|'USD'|'EUR'} moneda
 * @property {string} medioPagoId
 * @property {MedioPago} medioPago
 * @property {string|null} cuentaBancariaId
 * @property {CuentaBancaria|null} cuentaBancaria
 * @property {number} importe
 * @property {'PENDIENTE'|'PARCIAL'|'CERRADO'} estado
 * @property {string} observaciones
 * @property {Array<Imputacion>} imputaciones
 * @property {Array<Adjunto>} adjuntos
 * 
 * @typedef {Object} Imputacion
 * @property {string} comprobanteId
 * @property {string} comprobanteNumero
 * @property {number} monto
 * @property {number} saldoAnterior
 * 
 * @typedef {Object} Adjunto
 * @property {string} url
 * @property {string} nombre
 * 
 * @typedef {Object} Cobranza
 * @property {string} id
 * @property {string} fecha
 * @property {string} clienteId
 * @property {Object} cliente
 * @property {'ARS'|'USD'|'EUR'} moneda
 * @property {string} medioPagoId
 * @property {MedioPago} medioPago
 * @property {string|null} cuentaBancariaId
 * @property {CuentaBancaria|null} cuentaBancaria
 * @property {number} importe
 * @property {'PENDIENTE'|'PARCIAL'|'CERRADO'} estado
 * @property {string} observaciones
 * @property {Array<Imputacion>} imputaciones
 * @property {Array<Adjunto>} adjuntos
 */

import { gql } from '@urql/core';
import { useQuery, useMutation } from 'urql';
import { executeReceiptOperation, readReceiptOperation, startNewReceiptOperation } from '@/utils/receipt-operation.js';

// ============================================================================
// CUENTAS BANCARIAS - Queries & Mutations
// ============================================================================

/** Query: Obtener listado de cuentas bancarias con filtros */
export const GET_CUENTAS = gql`
  query GetCuentas(
    $search: String
    $estado: String
    $moneda: String
    $page: Int
    $pageSize: Int
    $sortBy: String
    $sortOrder: String
  ) {
    cuentasBancarias(
      search: $search
      estado: $estado
      moneda: $moneda
      page: $page
      pageSize: $pageSize
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        banco
        descripcion
        moneda
        tipo
        cbu
        alias
        numeroCuenta
        titular
        saldo
        estado
        observaciones
        createdAt
        updatedAt
      }
      pagination {
        total
        page
        pageSize
        totalPages
      }
    }
  }
`;

/** Query: Obtener una cuenta bancaria por ID */
export const GET_CUENTA = gql`
  query GetCuenta($id: ID!) {
    cuentaBancaria(id: $id) {
      id
      banco
      descripcion
      moneda
      tipo
      cbu
      alias
      numeroCuenta
      titular
      saldo
      estado
      observaciones
      createdAt
      updatedAt
    }
  }
`;

/** Mutation: Crear cuenta bancaria */
export const CREATE_CUENTA = gql`
  mutation CreateCuenta($input: CuentaBancariaInput!) {
    createCuentaBancaria(input: $input) {
      id
      banco
      descripcion
      moneda
      tipo
      cbu
      alias
      numeroCuenta
      titular
      saldo
      estado
      observaciones
    }
  }
`;

/** Mutation: Actualizar cuenta bancaria */
export const UPDATE_CUENTA = gql`
  mutation UpdateCuenta($id: ID!, $input: CuentaBancariaInput!) {
    updateCuentaBancaria(id: $id, input: $input) {
      id
      banco
      descripcion
      moneda
      tipo
      cbu
      alias
      numeroCuenta
      titular
      saldo
      estado
      observaciones
    }
  }
`;

/** Mutation: Eliminar cuenta bancaria */
export const DELETE_CUENTA = gql`
  mutation DeleteCuenta($id: ID!) {
    deleteCuentaBancaria(id: $id) {
      success
      message
    }
  }
`;

// ============================================================================
// MEDIOS DE PAGO - Queries & Mutations
// ============================================================================

/** Query: Obtener listado de medios de pago */
export const GET_MEDIOS = gql`
  query GetMediosPago(
    $search: String
    $tipo: String
    $estado: Boolean
    $page: Int
    $pageSize: Int
    $sortBy: String
    $sortOrder: String
  ) {
    mediosPago(
      search: $search
      tipo: $tipo
      estado: $estado
      page: $page
      pageSize: $pageSize
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        nombre
        tipo
        cuentaBancariaId
        cuentaBancaria {
          id
          banco
          descripcion
          moneda
        }
        habilitado
        notas
      }
      pagination {
        total
        page
        pageSize
        totalPages
      }
    }
  }
`;

/** Query: Obtener un medio de pago por ID */
export const GET_MEDIO = gql`
  query GetMedioPago($id: ID!) {
    medioPago(id: $id) {
      id
      nombre
      tipo
      cuentaBancariaId
      cuentaBancaria {
        id
        banco
        descripcion
        moneda
      }
      habilitado
      notas
    }
  }
`;

/** Mutation: Crear medio de pago */
export const CREATE_MEDIO = gql`
  mutation CreateMedioPago($input: MedioPagoInput!) {
    createMedioPago(input: $input) {
      id
      nombre
      tipo
      cuentaBancariaId
      habilitado
      notas
    }
  }
`;

/** Mutation: Actualizar medio de pago */
export const UPDATE_MEDIO = gql`
  mutation UpdateMedioPago($id: ID!, $input: MedioPagoInput!) {
    updateMedioPago(id: $id, input: $input) {
      id
      nombre
      tipo
      cuentaBancariaId
      habilitado
      notas
    }
  }
`;

/** Mutation: Eliminar medio de pago */
export const DELETE_MEDIO = gql`
  mutation DeleteMedioPago($id: ID!) {
    deleteMedioPago(id: $id) {
      success
      message
    }
  }
`;

// ============================================================================
// PAGOS A PROVEEDORES - Queries & Mutations
// ============================================================================

/** Query: Obtener listado de pagos */
export const GET_PAGOS = gql`
  query GetPagos(
    $fechaDesde: String
    $fechaHasta: String
    $proveedorId: ID
    $medioPagoId: ID
    $estado: String
    $moneda: String
    $page: Int
    $pageSize: Int
    $sortBy: String
    $sortOrder: String
  ) {
    pagos(
      fechaDesde: $fechaDesde
      fechaHasta: $fechaHasta
      proveedorId: $proveedorId
      medioPagoId: $medioPagoId
      estado: $estado
      moneda: $moneda
      page: $page
      pageSize: $pageSize
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        fecha
        proveedor {
          id
          nombre
          cuit
        }
        medioPago {
          id
          nombre
          tipo
        }
        cuentaBancaria {
          id
          banco
          descripcion
        }
        importe
        moneda
        estado
        observaciones
        imputaciones {
          comprobanteId
          comprobanteNumero
          monto
        }
        adjuntos {
          url
          nombre
        }
        createdAt
      }
      pagination {
        total
        page
        pageSize
        totalPages
      }
    }
  }
`;

/** Query: Obtener un pago por ID */
export const GET_PAGO = gql`
  query GetPago($id: ID!) {
    pago(id: $id) {
      id
      fecha
      proveedorId
      proveedor {
        id
        nombre
        cuit
      }
      medioPagoId
      medioPago {
        id
        nombre
        tipo
      }
      cuentaBancariaId
      cuentaBancaria {
        id
        banco
        descripcion
      }
      importe
      moneda
      estado
      observaciones
      imputaciones {
        comprobanteId
        comprobanteNumero
        monto
        saldoAnterior
      }
      adjuntos {
        url
        nombre
      }
      createdAt
      updatedAt
    }
  }
`;

/** Mutation: Crear pago */
export const CREATE_PAGO = gql`
  mutation CreatePago($input: PagoInput!) {
    createPago(input: $input) {
      id
      fecha
      proveedorId
      medioPagoId
      cuentaBancariaId
      importe
      moneda
      estado
      observaciones
      imputaciones {
        comprobanteId
        monto
      }
      adjuntos {
        url
        nombre
      }
    }
  }
`;

/** Mutation: Actualizar pago */
export const UPDATE_PAGO = gql`
  mutation UpdatePago($id: ID!, $input: PagoInput!) {
    updatePago(id: $id, input: $input) {
      id
      fecha
      proveedorId
      medioPagoId
      cuentaBancariaId
      importe
      moneda
      estado
      observaciones
      imputaciones {
        comprobanteId
        monto
      }
      adjuntos {
        url
        nombre
      }
    }
  }
`;

/** Mutation: Eliminar pago */
export const DELETE_PAGO = gql`
  mutation DeletePago($id: ID!) {
    deletePago(id: $id) {
      success
      message
    }
  }
`;

// ============================================================================
// CLIENTES - Consulta básica para cobranzas
// ============================================================================

export const GET_CLIENTES_COBRANZAS = gql`
  query ClientesCobranzas($page: Int, $pageSize: Int, $filter: ClienteFilter) {
    clientes(page: $page, pageSize: $pageSize, filter: $filter) {
      totalCount
      page
      pageSize
      edges {
        node {
          id
          razonSocial
          cuit
          localidad {
            nombre
          }
        }
      }
    }
  }
`;

// ============================================================================
// COBRANZAS A CLIENTES - Queries & Mutations
// ============================================================================

/** Query: Obtener listado de cobranzas */
export const GET_COBRANZAS = gql`
  query Cobranzas($page: Int, $pageSize: Int, $filter: CobranzaFilter) {
    cobranzas(page: $page, pageSize: $pageSize, filter: $filter) {
      totalCount
      page
      pageSize
      items {
        id
        fechaCobranza
        totalCobrado
        metodoCobranza
        observaciones
        sucursalCobroId
        sucursalCobroNombre
        cliente {
          id
          razonSocial
          cuit
        }
      }
    }
  }
`;

/** Query: Obtener una cobranza por ID */
export const GET_COBRANZA = gql`
  query Cobranza($id: ID!) {
    cobranza(id: $id) {
      id
      fechaCobranza
      totalCobrado
      metodoCobranza
      observaciones
      sucursalCobroId
      sucursalCobroNombre
      cliente {
        id
        razonSocial
        cuit
      }
      detalles {
        id
        facturaId
        importeAplicado
        facturaNumero
        facturaFecha
        facturaTotal
        facturaSaldo
      }
      cheques {
        id
        numeroCheque
        codigoBanco
        importe
        fechaEmision
        fechaCobro
        fechaDeposito
        sucursalOrigenId
        sucursalActualId
        estado
        imagenUrl
      }
    }
  }
`;

/** Query: Facturas pendientes por cliente */
export const GET_FACTURAS_PENDIENTES = gql`
  query FacturasPendientesPorCliente($clienteId: ID!) {
    facturasPendientesPorCliente(clienteId: $clienteId) {
      saldoPendienteTotal
      facturas {
        id
        numero
        fechaEmision
        fechaVencimiento
        total
        saldoPendiente
        estado
      }
    }
  }
`;

/** Mutation: Registrar cobranza */
export const REGISTRAR_COBRANZA = gql`
  mutation RegistrarCobranza($input: RegistrarCobranzaInput!) {
    registrarCobranza(input: $input) {
      cobranza {
        id
        fechaCobranza
        totalCobrado
        metodoCobranza
        sucursalCobroNombre
      }
      detalles {
        id
        facturaId
        importeAplicado
        facturaNumero
      }
      cheques {
        id
        numeroCheque
        codigoBanco
        importe
        fechaCobro
        estado
      }
      resumen {
        totalPendiente
        totalCobrado
        totalAplicado
        saldoCuenta
        saldoPendiente
      }
    }
  }
`;

// ============================================================================
// HOOKS PERSONALIZADOS (con datos dummy mientras se conecta el backend)
// ============================================================================

/**
 * Hook para obtener cuentas bancarias
 * TODO: Descomentar la query real cuando el backend esté listo
 */
export const useCuentas = (variables = {}) => {
  // const [result, reexecuteQuery] = useQuery({
  //   query: GET_CUENTAS,
  //   variables,
  // });

  // Datos dummy temporales
  const dummyData = {
    data: {
      cuentasBancarias: {
        data: [
          {
            id: '1',
            banco: 'Banco Galicia',
            descripcion: 'Cuenta Operativa Principal',
            moneda: 'ARS',
            tipo: 'CC',
            cbu: '0070999630000012345678',
            alias: 'SINAMON.SOFT.ARS',
            numeroCuenta: '12345678',
            titular: 'Sinamon Soft SRL',
            saldo: 1250000.50,
            estado: 'ACTIVO',
            observaciones: 'Cuenta principal de operaciones',
            createdAt: '2025-01-15T10:00:00Z',
            updatedAt: '2025-10-30T15:30:00Z',
          },
          {
            id: '2',
            banco: 'Banco Santander',
            descripcion: 'Cuenta USD Exportación',
            moneda: 'USD',
            tipo: 'CA',
            cbu: '0720999688000087654321',
            alias: 'SINAMON.USD.EXPORT',
            numeroCuenta: '87654321',
            titular: 'Sinamon Soft SRL',
            saldo: 45000.00,
            estado: 'ACTIVO',
            observaciones: 'Cuenta para cobros en USD',
            createdAt: '2025-02-01T09:00:00Z',
            updatedAt: '2025-10-29T11:20:00Z',
          },
          {
            id: '3',
            banco: 'BBVA',
            descripcion: 'Cuenta Secundaria',
            moneda: 'ARS',
            tipo: 'CA',
            cbu: '0170999620000011223344',
            alias: 'SINAMON.BBVA.SEC',
            numeroCuenta: '11223344',
            titular: 'Sinamon Soft SRL',
            saldo: 350000.75,
            estado: 'ACTIVO',
            observaciones: '',
            createdAt: '2025-03-10T14:00:00Z',
            updatedAt: '2025-10-28T16:45:00Z',
          },
        ],
        pagination: {
          total: 3,
          page: 1,
          pageSize: 10,
          totalPages: 1,
        },
      },
    },
    fetching: false,
    error: undefined,
  };

  return [dummyData, () => {}];
};

/**
 * Hook para obtener una cuenta bancaria específica
 */
export const useCuenta = (id) => {
  // const [result] = useQuery({
  //   query: GET_CUENTA,
  //   variables: { id },
  //   pause: !id,
  // });

  const allCuentas = useCuentas()[0].data?.cuentasBancarias?.data || [];
  const cuenta = allCuentas.find((c) => c.id === id);

  return [
    {
      data: cuenta ? { cuentaBancaria: cuenta } : null,
      fetching: false,
      error: undefined,
    },
  ];
};

/**
 * Hook para crear/actualizar/eliminar cuentas bancarias
 */
export const useCuentaMutations = () => {
  // const [, createCuenta] = useMutation(CREATE_CUENTA);
  // const [, updateCuenta] = useMutation(UPDATE_CUENTA);
  // const [, deleteCuenta] = useMutation(DELETE_CUENTA);

  const createCuenta = async (variables) => {
    // Simular delay de red
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      data: {
        createCuentaBancaria: {
          id: Date.now().toString(),
          ...variables.input,
          saldo: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      },
      error: undefined,
    };
  };

  const updateCuenta = async (variables) => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      data: {
        updateCuentaBancaria: {
          id: variables.id,
          ...variables.input,
          updatedAt: new Date().toISOString(),
        },
      },
      error: undefined,
    };
  };

  const deleteCuenta = async (variables) => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      data: {
        deleteCuentaBancaria: {
          success: true,
          message: 'Cuenta eliminada correctamente',
        },
      },
      error: undefined,
    };
  };

  return { createCuenta, updateCuenta, deleteCuenta };
};

/**
 * Hook para obtener medios de pago
 */
export const useMediosPago = (variables = {}) => {
  const dummyData = {
    data: {
      mediosPago: {
        data: [
          {
            id: '1',
            nombre: 'Efectivo',
            tipo: 'EFECTIVO',
            cuentaBancariaId: null,
            cuentaBancaria: null,
            habilitado: true,
            notas: 'Pago en efectivo',
          },
          {
            id: '2',
            nombre: 'Transferencia Galicia',
            tipo: 'TRANSFERENCIA',
            cuentaBancariaId: '1',
            cuentaBancaria: {
              id: '1',
              banco: 'Banco Galicia',
              descripcion: 'Cuenta Operativa Principal',
              moneda: 'ARS',
            },
            habilitado: true,
            notas: 'Transferencias a cuenta principal',
          },
          {
            id: '3',
            nombre: 'Moni',
            tipo: 'MONI',
            cuentaBancariaId: '1',
            cuentaBancaria: {
              id: '1',
              banco: 'Banco Galicia',
              descripcion: 'Cuenta Operativa Principal',
              moneda: 'ARS',
            },
            habilitado: true,
            notas: 'Cobros digitales Moni',
          },
          {
            id: '4',
            nombre: 'Tarjeta Visa/Mastercard',
            tipo: 'TARJETA',
            cuentaBancariaId: '3',
            cuentaBancaria: {
              id: '3',
              banco: 'BBVA',
              descripcion: 'Cuenta Secundaria',
              moneda: 'ARS',
            },
            habilitado: true,
            notas: 'Cobros con tarjeta',
          },
        ],
        pagination: {
          total: 4,
          page: 1,
          pageSize: 10,
          totalPages: 1,
        },
      },
    },
    fetching: false,
    error: undefined,
  };

  return [dummyData, () => {}];
};

/**
 * Hook para obtener un medio de pago específico
 */
export const useMedioPago = (id) => {
  const allMedios = useMediosPago()[0].data?.mediosPago?.data || [];
  const medio = allMedios.find((m) => m.id === id);

  return [
    {
      data: medio ? { medioPago: medio } : null,
      fetching: false,
      error: undefined,
    },
  ];
};

/**
 * Hook para crear/actualizar/eliminar medios de pago
 */
export const useMedioPagoMutations = () => {
  const createMedio = async (variables) => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      data: {
        createMedioPago: {
          id: Date.now().toString(),
          ...variables.input,
        },
      },
      error: undefined,
    };
  };

  const updateMedio = async (variables) => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      data: {
        updateMedioPago: {
          id: variables.id,
          ...variables.input,
        },
      },
      error: undefined,
    };
  };

  const deleteMedio = async (variables) => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      data: {
        deleteMedioPago: {
          success: true,
          message: 'Medio de pago eliminado correctamente',
        },
      },
      error: undefined,
    };
  };

  return { createMedio, updateMedio, deleteMedio };
};

/**
 * Hook para obtener pagos
 */
export const usePagos = (variables = {}) => {
  const dummyData = {
    data: {
      pagos: {
        data: [
          {
            id: '1',
            fecha: '2025-10-28',
            proveedor: {
              id: 'prov1',
              nombre: 'Distribuidora San Martín',
              cuit: '30-12345678-9',
            },
            medioPago: {
              id: '2',
              nombre: 'Transferencia Galicia',
              tipo: 'TRANSFERENCIA',
            },
            cuentaBancaria: {
              id: '1',
              banco: 'Banco Galicia',
              descripcion: 'Cuenta Operativa Principal',
            },
            importe: 250000.00,
            moneda: 'ARS',
            estado: 'CERRADO',
            observaciones: 'Pago factura octubre',
            imputaciones: [
              {
                comprobanteId: 'fc1',
                comprobanteNumero: 'FC-0001-00012345',
                monto: 250000.00,
              },
            ],
            adjuntos: [],
            createdAt: '2025-10-28T10:30:00Z',
          },
          {
            id: '2',
            fecha: '2025-10-29',
            proveedor: {
              id: 'prov2',
              nombre: 'Logística del Norte SA',
              cuit: '30-98765432-1',
            },
            medioPago: {
              id: '1',
              nombre: 'Efectivo',
              tipo: 'EFECTIVO',
            },
            cuentaBancaria: null,
            importe: 50000.00,
            moneda: 'ARS',
            estado: 'PARCIAL',
            observaciones: 'Anticipo 50%',
            imputaciones: [
              {
                comprobanteId: 'fc2',
                comprobanteNumero: 'FC-0001-00067890',
                monto: 50000.00,
              },
            ],
            adjuntos: [
              {
                url: 'https://example.com/recibo1.pdf',
                nombre: 'Recibo Anticipo',
              },
            ],
            createdAt: '2025-10-29T14:15:00Z',
          },
        ],
        pagination: {
          total: 2,
          page: 1,
          pageSize: 10,
          totalPages: 1,
        },
      },
    },
    fetching: false,
    error: undefined,
  };

  return [dummyData, () => {}];
};

/**
 * Hook para obtener un pago específico
 */
export const usePago = (id) => {
  const allPagos = usePagos()[0].data?.pagos?.data || [];
  const pago = allPagos.find((p) => p.id === id);

  return [
    {
      data: pago ? { pago } : null,
      fetching: false,
      error: undefined,
    },
  ];
};

/**
 * Hook para crear/actualizar/eliminar pagos
 */
export const usePagoMutations = () => {
  const createPago = async (variables) => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      data: {
        createPago: {
          id: Date.now().toString(),
          ...variables.input,
          createdAt: new Date().toISOString(),
        },
      },
      error: undefined,
    };
  };

  const updatePago = async (variables) => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      data: {
        updatePago: {
          id: variables.id,
          ...variables.input,
          updatedAt: new Date().toISOString(),
        },
      },
      error: undefined,
    };
  };

  const deletePago = async (variables) => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      data: {
        deletePago: {
          success: true,
          message: 'Pago eliminado correctamente',
        },
      },
      error: undefined,
    };
  };

  return { createPago, updatePago, deletePago };
};

/**
 * Hook para obtener cobranzas
 */
export const useCobranzas = (variables = {}) => {
  return useQuery({ query: GET_COBRANZAS, variables, requestPolicy: 'network-only' });
};

/**
 * Hook para obtener una cobranza específica
 */
export const useCobranza = (id) => {
  return useQuery({
    query: GET_COBRANZA,
    variables: { id },
    pause: !id,
    requestPolicy: 'network-only',
  });
};

/**
 * Hook para crear cobranzas
 */
export const useRegistrarCobranza = () => {
  const [state, mutate] = useMutation(REGISTRAR_COBRANZA);
  const register = async (variables, context) => {
    try {
      return await executeReceiptOperation('graphql:registrar', variables?.input, async (input, key, assertScope) => {
        const previousOptions = context?.fetchOptions;
        const result = await mutate({ ...variables, input: { ...input, idempotencyKey: key } }, {
          ...context,
          fetchOptions: () => ({
            ...(typeof previousOptions === 'function' ? previousOptions() : previousOptions),
            receiptOperationGuard: assertScope,
          }),
        });
        if (result.error || !result.data?.registrarCobranza?.cobranza?.id) throw result.error || new Error('No se recibió confirmación de la cobranza.');
        return result;
      });
    } catch (error) {
      return { data: undefined, error };
    }
  };
  return [state, register, {
    pending: () => readReceiptOperation('graphql:registrar'),
    newOperation: () => startNewReceiptOperation('graphql:registrar'),
    resume: (context) => register(undefined, context),
  }];
};

/**
 * Compatibilidad: wrapper para alta/edición de cobranzas existentes
 */
export const useCobranzaMutations = () => {
  const [state, registrarCobranza, receiptOperation] = useRegistrarCobranza();

  const createCobranza = async (variables) => {
    return registrarCobranza(variables);
  };

  const updateCobranza = async () => {
    return {
      data: null,
      error: { message: 'La edición de cobranzas no está disponible en este módulo.' },
    };
  };

  const deleteCobranza = async () => {
    return {
      data: null,
      error: { message: 'La eliminación de cobranzas no está disponible en este módulo.' },
    };
  };

  return { createCobranza, updateCobranza, deleteCobranza, state, receiptOperation };
};

/**
 * Hook para buscar clientes en cobranzas
 */
export const useClientesCobranzas = (variables = {}, pause = false) => {
  return useQuery({
    query: GET_CLIENTES_COBRANZAS,
    variables,
    pause,
    requestPolicy: 'network-only',
  });
};

/**
 * Hook para obtener facturas pendientes por cliente
 */
export const useFacturasPendientesPorCliente = (clienteId) => {
  return useQuery({
    query: GET_FACTURAS_PENDIENTES,
    variables: { clienteId },
    pause: !clienteId,
    requestPolicy: 'network-only',
  });
};
